import { createContext, useContext, useState, useEffect, useCallback, type ReactNode } from 'react';
import { loginWithPasskey, loginWithPassword, getCurrentUser, logout as logoutService, type User, AuthError } from '../lib/auth';

interface AuthContextType {
  user: User | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  loginWithPasskey: (email: string) => Promise<void>;
  loginWithPassword: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  checkAuth: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    try {
      const currentUser = await getCurrentUser();
      setUser(currentUser);
    } catch {
      setUser(null);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const handleLoginWithPasskey = useCallback(async (email: string) => {
    setIsLoading(true);
    try {
      const loggedInUser = await loginWithPasskey(email);
      setUser(loggedInUser);
    } catch (error) {
      setIsLoading(false);
      if (error instanceof AuthError) {
        throw error;
      }
      throw new AuthError('Error de autenticacion', 'UNKNOWN_ERROR');
    } finally {
      setIsLoading(false);
    }
  }, []);

  const handleLoginWithPassword = useCallback(async (email: string, password: string) => {
    setIsLoading(true);
    try {
      const loggedInUser = await loginWithPassword(email, password);
      setUser(loggedInUser);
    } catch (error) {
      setIsLoading(false);
      if (error instanceof AuthError) {
        throw error;
      }
      throw new AuthError('Error de autenticacion', 'UNKNOWN_ERROR');
    } finally {
      setIsLoading(false);
    }
  }, []);

  const logout = useCallback(async () => {
    try {
      await logoutService();
    } finally {
      setUser(null);
    }
  }, []);

  return (
    <AuthContext.Provider
      value={{
        user,
        isLoading,
        isAuthenticated: !!user,
        loginWithPasskey: handleLoginWithPasskey,
        loginWithPassword: handleLoginWithPassword,
        logout,
        checkAuth,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

export type { User };
